import React, {
  ComponentProps,
  PropsWithChildren,
  useEffect,
  useRef,
} from "react";
import { ModelViewerElement } from "@google/model-viewer/dist/model-viewer";

/** This is only used to disable the poster at the moment. If we even want to customize the poster, more work will need to be done. */
export function Poster(props: ComponentProps<"div">) {
  return <div {...props} slot="poster" />;
}

/** This is only used to disable the progress bar at the moment. If we even want to customize the progress bar, more work will need to be done. */
export function ProgressBar(props: ComponentProps<"div">) {
  return <div {...props} slot="progress-bar" />;
}

/** Pulled from https://modelviewer.dev/docs/index.html#loading-events */
type Events =
  | "load"
  | "error"
  | "posterDismissed"
  | "modelVisibility"
  | "progress"
  | "renderScale";

type MappedEvents = {
  [k in Events as `on${Capitalize<k>}`]: (this: HTMLElement) => void;
};

export type GoogleModelViewerProps = Partial<
  Omit<ModelViewerElement, "children">
> &
  Partial<MappedEvents> & {
    className?: string;
  };

/**
 * 3D Model Viewer from Google. See API Reference below for a list of props.
 *
 * Only use this component if you need the viewer loaded immediately on page load. If the viewer can be loaded later, preferably when viewed, then use the `LazyLoadedGoogleModelViewer` component.
 *
 * @see {@link https://modelviewer.dev/docs/index.html API Reference}
 */
export function GoogleModelViewer({
  className,
  onLoad,
  onPosterDismissed,
  onError,
  onProgress,
  onRenderScale,
  onModelVisibility,
  children,
  ...rest
}: PropsWithChildren<GoogleModelViewerProps>): JSX.Element {
  const ref = useRef<ModelViewerElement>(null);

  useEffect(
    function initEvents() {
      if (!ref.current) return;
      const elem = ref.current;
      const controller = new AbortController();
      if (onLoad) {
        elem.addEventListener("load", onLoad, {
          signal: controller.signal,
        });
      }
      if (onPosterDismissed) {
        elem.addEventListener("poster-dismissed", onPosterDismissed, {
          signal: controller.signal,
        });
      }
      if (onModelVisibility) {
        elem.addEventListener("model-visibility", onModelVisibility, {
          signal: controller.signal,
        });
      }
      if (onError) {
        elem.addEventListener("error", onError, {
          signal: controller.signal,
        });
      }
      if (onProgress) {
        elem.addEventListener("progress", onProgress, {
          signal: controller.signal,
        });
      }
      if (onRenderScale) {
        elem.addEventListener("render-scale", onRenderScale, {
          signal: controller.signal,
        });
      }
      return function cleanupInitEvents() {
        controller.abort();
      };
    },
    [
      onError,
      onLoad,
      onModelVisibility,
      onPosterDismissed,
      onProgress,
      onRenderScale,
    ],
  );

  return React.createElement(
    "model-viewer",
    {
      "aria-label":
        "Rotate with the option key plus the arrow keys or zoom with page up and page down.",
      "auto-rotate": true,
      "camera-controls": true,
      /** Add attributes that can be overwritten above this line */
      ...rest,
      /** Add attributes that are fixed below this line */
      class: className,
      "shadow-intensity": 1,
      "shadow-softness": 0.9,
      exposure: 0.85,
      "tone-mapping": "commerce",
      ref,
    },
    <>
      {children}
      <button
        type="button"
        slot="hotspot-dot+X-Y+Z"
        className="dot hide"
        data-position="1 -1 1"
        data-normal="1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dim+X-Y"
        className="dim hide xs:text-xs text-sm font-bold"
        data-position="1 -1 0"
        data-normal="1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dot+X-Y-Z"
        className="dot hide"
        data-position="1 -1 -1"
        data-normal="1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dim+X-Z"
        className="dim hide xs:text-xs text-sm font-bold"
        data-position="1 0 -1"
        data-normal="1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dot+X+Y-Z"
        className="dot hide"
        data-position="1 1 -1"
        data-normal="0 1 0"
      />
      <button
        type="button"
        slot="hotspot-dim+Y-Z"
        className="dim hide xs:text-xs text-sm font-bold"
        data-position="0 -1 -1"
        data-normal="0 1 0"
      />
      <button
        type="button"
        slot="hotspot-dot-X+Y-Z"
        className="dot hide"
        data-position="-1 1 -1"
        data-normal="0 1 0"
      />
      <button
        type="button"
        slot="hotspot-dim-X-Z"
        className="dim hide xs:text-xs text-sm font-bold"
        data-position="-1 0 -1"
        data-normal="-1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dot-X-Y-Z"
        className="dot hide"
        data-position="-1 -1 -1"
        data-normal="-1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dim-X-Y"
        className="dim hide xs:text-xs text-sm font-bold"
        data-position="-1 -1 0"
        data-normal="-1 0 0"
      />
      <button
        type="button"
        slot="hotspot-dot-X-Y+Z"
        className="dot hide"
        data-position="-1 -1 1"
        data-normal="-1 0 0"
      />
      <svg
        id="dimLines"
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        className="dimensionLineContainer"
      >
        <line className="dimensionLine" />
        <line className="dimensionLine" />
        <line className="dimensionLine" />
        <line className="dimensionLine" />
        <line className="dimensionLine" />
      </svg>
    </>,
  );
}
